import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { SelectSearchBase } from '../fi-select-search/select-search-base';
import { FiDropdownItemDisabledFn } from '../fi-select-search/fi-select-search';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { isDefined } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';
import { fiExecuteOnChildItems } from '@fi-shared/utils/fi-items-flatten';

interface ISelectSearchExpandableItemData {
    isExpanded: boolean;
    isVisibleByExpand: boolean;
    isVisibleBySearch: boolean;
}

export interface IMenuExpandableMultiSelectItem<T extends IMenuExpandableMultiSelectItem<T>> {
    Id: number;
    ObjectType: ObjectTypes;
    Level: number;
    ChildItems: T[];
}

@Component({
    selector: 'fi-menu-expandable-multi-select-item-search',
    templateUrl: './fi-menu-expandable-multi-select-item-search.component.html',
    styleUrls: [
        './fi-menu-expandable-multi-select-item-search.component.scss',
        '../fi-menu-multi-select-item-search/fi-menu-multi-select-item-search.component.scss',
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-menu-expandable-multi-select-item-search' },
    standalone: false,
})
export class FiMenuExpandableMultiSelectItemSearchComponent<T extends IMenuExpandableMultiSelectItem<T>>
    extends SelectSearchBase<T, T[]>
    implements OnInit, OnChanges
{
    @Input({ required: true }) items: T[] = [];
    @Input({ required: true }) selectionObjectTypes: ObjectTypes[] = [];
    @Input({ required: true }) expandableObjectTypes: ObjectTypes[] = [];
    @Input() displayValue: string = 'Name';
    @Input() isProfileIconShown: boolean;
    @Input() profileIconName: string;
    @Input() externalFilterFn: FiDropdownItemDisabledFn<T>;
    @Input() initialExpandState = false;

    protected listItemBasePadding: number = 0;
    protected listItemPaddingPerLevel: number = 0;

    private itemsDict: {
        [objectType: number]: {
            [id: number]: ISelectSearchExpandableItemData;
        };
    };

    constructor(injector: Injector) {
        super(injector);
        this.isDynamicItemHeight = true;
    }

    ngOnChanges(changes: SimpleChanges) {
        const change = changes.items;
        if (change && change.currentValue !== change.previousValue) {
            this.updateItemData();
        }

        super.ngOnChanges(changes);
    }

    protected getSelectedItemsFromModel(): T[] {
        return this.ngModel.slice();
    }

    protected updateFilteredItems() {
        this.updateVisibilityBySearch();
        this.filteredItems$.next(this.getFilteredItems());
    }

    private getFilteredItems(useOnlyExternalFilter?: boolean): T[] {
        if (!this.searchText && !isDefined(this.externalFilterFn)) return this.items;

        let filteredItems: T[] = this.items.filter((item) => {
            if (!useOnlyExternalFilter) {
                const itemData = this.getItemData(item);
                if (itemData && (!itemData.isVisibleByExpand || !itemData.isVisibleBySearch)) {
                    // parent item collapsed or item not in search results
                    return false;
                }
            }
            if (this.externalFilterFn) {
                return this.externalFilterFn(item, this.items);
            }
            return true;
        });

        return filteredItems;
    }

    private updateVisibilityBySearch() {
        // reset isVisibleBySearch for all items
        let isSearchEmpty = !this.searchText;
        for (let objectType in this.itemsDict) {
            const objectTypeDict = this.itemsDict[objectType];
            for (let id in objectTypeDict) {
                objectTypeDict[id].isVisibleBySearch = isSearchEmpty;
            }
        }
        if (!isSearchEmpty) {
            const items = this.getFilteredItems(true);
            let matchedItems = items.filter((item) => {
                return item[this.displayValue].toLowerCase().includes(this.searchText.toLowerCase());
            });
            if (!matchedItems.length) return;

            items.forEach((item) => {
                const itemData = this.getItemData(item);
                const isMatched = matchedItems.includes(item);
                if (isMatched) {
                    itemData.isVisibleBySearch = true;
                }

                fiExecuteOnChildItems(item, (childItem) => {
                    const childData = this.getItemData(childItem);
                    if (isMatched) {
                        // if item is visible all children should be also visible
                        childData.isVisibleBySearch = true;
                    } else if (!itemData.isVisibleBySearch) {
                        // check if some child is visible by search
                        if (matchedItems.includes(childItem)) {
                            itemData.isVisibleBySearch = true;
                        }
                    }
                });
            });
        }
    }

    private getSelectedItem(item: T): T {
        return this.ngModel.find((x) => x.Id === item.Id && x.ObjectType === item.ObjectType);
    }

    isItemSelected(item: T): boolean {
        return !!this.getSelectedItem(item);
    }

    onItemClick($event: MouseEvent, item: T) {
        if (this.isOptionDisabled(item) || !this.isCheckboxVisible(item)) {
            return;
        }
        $event.stopPropagation();

        const selectedItem = this.getSelectedItem(item);
        const itemIndex = isDefined(selectedItem) ? this.ngModel.indexOf(selectedItem) : -1;

        if (itemIndex === -1) {
            this.ngModel.push(item);
        } else {
            this.ngModel.splice(itemIndex, 1);
        }
        this.doPropagateChanges();
    }

    isCheckboxVisible(item: T) {
        return this.selectionObjectTypes.includes(item.ObjectType);
    }

    //# region expand

    canExpand(item: T) {
        return this.expandableObjectTypes.includes(item.ObjectType);
    }

    isExpanded(item: T) {
        return this.getItemData(item).isExpanded;
    }

    toggleExpand($event: MouseEvent, item: T) {
        $event.stopPropagation();
        const itemData = this.getItemData(item);
        itemData.isExpanded = !itemData.isExpanded;
        this.updateVisibilityOnExpandChange(item);
        this.updateFilteredItems();
    }

    private updateItemData() {
        const filteredItems = this.getFilteredItems(true);
        this.itemsDict = {};
        filteredItems.forEach((item) => {
            if (!this.itemsDict[item.ObjectType]) {
                this.itemsDict[item.ObjectType] = {};
            }
            if (!this.itemsDict[item.ObjectType][item.Id]) {
                this.itemsDict[item.ObjectType][item.Id] = {
                    isVisibleByExpand: true,
                    isVisibleBySearch: true,
                    isExpanded: this.canExpand(item) ? this.initialExpandState : undefined,
                };
            }
        });
        filteredItems
            .filter((item) => this.canExpand(item))
            .forEach((item) => {
                this.updateVisibilityOnExpandChange(item);
            });
    }

    private updateVisibilityOnExpandChange(item: T) {
        const itemData = this.getItemData(item);
        fiExecuteOnChildItems(item, (childItem) => {
            const childData = this.getItemData(childItem);
            childData.isVisibleByExpand = itemData.isExpanded;
            if (this.canExpand(childItem)) {
                childData.isExpanded = itemData.isExpanded;
            }
        });
    }

    private getItemData(item: T): ISelectSearchExpandableItemData {
        return this.itemsDict[item.ObjectType][item.Id];
    }

    //# endregion

    protected readonly FiProfileSizes = FiProfileSizes;
}
